import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import * as Sentry from '@sentry/react';

import { Controller, useWatch } from 'react-hook-form';
//Components
import InputText from '../../inputs/InputText';
import { ArrowRadioGroup } from '../../../pages/main/comunications/NewComunication/styles';

import DropDownInput from '../../inputs/DropDown/DropDownInput';
import SearchableDropdown from '../../inputs/SearchableDropdown';
// Styled Components
import { ErrorMessage, ContainerDescription } from './styles';

import {
  Title,
  InputTextAreaStyled,
  InputsContainer,
  PkInputContainer,
  LocationInputGroupCommunication,
  RoadInputSubGroup,
  ConcessionWrapper,
} from '../components/MainStyled';
// Types
import { CommunicationDirection, Margin, NewCommunication } from '../../../types/communication';
import { FormProps, GroupTypes } from './types';
import { OptionSelected } from '../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../inputs/DropDown/DropDownInput/types';
import { ImperativeRefSearchableDropdown } from '../../inputs/SearchableDropdown/types';

import { type PartialDirection } from '../../inputs/buttons/ArrowDirectionButton/types';
// Hooks
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
// GraphQL
import { client } from '../../../gql/client';

import {
  GET_CONCESSION_BY_ID,
  GET_ROAD_BY_ID,
  GET_ROADS_BY_IDS,
} from '../../../gql/queries/geoPositions/roads';
// Utils
import { pkFormatter } from '../../../utils/transform';
import { fetchDefaultData } from '../../../utils/fetchData';
import { groupLanes, groupTracks } from '../../../data/orders';
import {
  laneLabel,
  trackLabel,
  translateLaneOptions,
  translateTrackOptions,
  type CatalogEntry,
} from '../../../utils/catalogLabels';
import { UNSPECIFIC } from '../../../types/utils';
import { registerPk } from '../../../utils/pkField';

export const Description = ({
  control,
  setValue,
  register,
  errors,
  initialData,
  autoFillData,
}: FormProps) => {
  const { t } = useTranslation(['forms', 'common']);
  // Feature flag para mostrar/ocultar campo margen
  const showMargin = useFeatureFlag('showMargin', true);

  // Refs for dropdowns (needed for auto-fill)
  const concessionDropDownRef = useRef<ImperativeRefInput>(null);
  const trackDropDownRef = useRef<ImperativeRefSearchableDropdown>(null);
  const laneDropDownRef = useRef<ImperativeRefSearchableDropdown>(null);

  const roads = useWatch({
    control,
    name: 'roads',
  });
  const city = useWatch({
    control,
    name: 'city',
  });
  const pkInitRoad = useWatch({
    control,
    name: 'pkInitRoad',
  });
  const pkEndRoad = useWatch({
    control,
    name: 'pkEndRoad',
  });

  const roadDirections = useWatch({
    control,
    name: 'roadDirections',
  });

  const roadDropDownRef = useRef<ImperativeRefInput>(null);
  const onSelectConcession = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const {
          data: { concession },
        } = await client.query({
          query: GET_CONCESSION_BY_ID,
          variables: { id: option?.value },
        });

        setValue('city', concession.location);

        const { readRoads } = client.readQuery({
          query: GET_ROADS_BY_IDS,
          variables: { ids: concession.roadIDs ?? [] },
        });
        let finalRoads = readRoads;
        if (readRoads.length === 0) {
          await fetchDefaultData();
          const result = client.readQuery({
            query: GET_ROADS_BY_IDS,
            variables: { ids: concession.roadIDs ?? [] },
          });
          finalRoads = result?.readRoads ?? [];
        }
        setValue('roads', finalRoads);

        // Autoseleccionar primera carretera
        if (finalRoads && finalRoads.length > 0) {
          const firstRoad = finalRoads[0];
          setValue('road', firstRoad.id);
          roadDropDownRef.current?.setSelectedOption({
            label: firstRoad.name,
            value: firstRoad.id,
          });
          setValue(
            'pkInitRoad',
            pkFormatter(firstRoad.pkInitKm, firstRoad.pkInitMeter, true)
          );
          setValue(
            'pkEndRoad',
            pkFormatter(firstRoad.pkEndKm, firstRoad.pkEndMeter, true)
          );
          setValue('roadDirections', firstRoad.directions);
        } else {
          roadDropDownRef.current?.clearSelected();
          setValue('road', '');
        }
        onChange(option?.value ?? '');
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  const onSelectRoad = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const {
          data: { road },
        } = await client.query({
          query: GET_ROAD_BY_ID,
          variables: { id: option?.value },
        });

        setValue(
          'pkInitRoad',
          pkFormatter(road.pkInitKm, road.pkInitMeter, true)
        );
        setValue('pkEndRoad', pkFormatter(road.pkEndKm, road.pkEndMeter, true));
        setValue('roadDirections', road.directions);

        onChange(option?.value ?? '');
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  // Replicar lógica de onSelectConcession para autoselección y carga de roads al editar
  useLayoutEffect(() => {
    const concessions = control._defaultValues?.concessions;
    const preselectedConcessionId = control._defaultValues?.concession;
    const preselectedRoadId = control._defaultValues?.road;
    const isEditing = !!preselectedConcessionId;

    // Función que replica EXACTAMENTE la lógica de onSelectConcession
    const loadConcessionData = async (
      concessionId: string,
      shouldClearRoad: boolean
    ) => {
      try {
        // 1. Query para obtener datos de la concesión
        const {
          data: { concession },
        } = await client.query({
          query: GET_CONCESSION_BY_ID,
          variables: { id: concessionId },
        });

        // 2. Establecer city
        setValue('city', concession.location);

        // 3. Leer roads del cache
        let readRoads = client.readQuery({
          query: GET_ROADS_BY_IDS,
          variables: { ids: concession.roadIDs ?? [] },
        })?.readRoads;

        // 4. Si no hay roads en cache, fetchear y releer
        if (!readRoads || readRoads.length === 0) {
          await fetchDefaultData();
          readRoads = client.readQuery({
            query: GET_ROADS_BY_IDS,
            variables: { ids: concession.roadIDs ?? [] },
          })?.readRoads;
        }

        // 5. Establecer roads y autoseleccionar primera carretera
        if (readRoads && readRoads.length > 0) {
          setValue('roads', readRoads);

          // 6. Autoseleccionar primera carretera si es creación nueva (shouldClearRoad=true)
          if (shouldClearRoad) {
            const firstRoad = readRoads[0];
            setValue('road', firstRoad.id);
            roadDropDownRef.current?.setSelectedOption({
              label: firstRoad.name,
              value: firstRoad.id,
            });
            // Cargar PKs y directions de la primera carretera
            setValue(
              'pkInitRoad',
              pkFormatter(firstRoad.pkInitKm, firstRoad.pkInitMeter, true)
            );
            setValue(
              'pkEndRoad',
              pkFormatter(firstRoad.pkEndKm, firstRoad.pkEndMeter, true)
            );
            setValue('roadDirections', firstRoad.directions);
          }
        } else if (shouldClearRoad) {
          // Si no hay roads, limpiar
          roadDropDownRef.current?.clearSelected();
          setValue('road', '');
        }

        // 7. Establecer el valor de concession en el formulario
        setValue('concession', concessionId);

        // 8. Si es edición y hay road preseleccionada, cargar sus directions
        if (!shouldClearRoad && preselectedRoadId) {
          const {
            data: { road },
          } = await client.query({
            query: GET_ROAD_BY_ID,
            variables: { id: preselectedRoadId },
          });
          setValue('roadDirections', road.directions);
        }
      } catch (err) {
        Sentry.captureException(err);
        console.log('Error loading concession data:', err);
      }
    };

    // CASO 1: Nueva comunicación con una sola concesión disponible
    if (!isEditing && concessions?.length === 1) {
      const concession = concessions[0];
      const name = concession?.name;
      const id = concession?.id;

      if (name && id && name !== 'CX') {
        // Autoseleccionar y limpiar road
        loadConcessionData(id, true);
      }
      return;
    }

    // CASO 2: Editando - cargar roads de la concesión preseleccionada
    if (isEditing) {
      // NO limpiar road porque ya está preseleccionada
      loadConcessionData(preselectedConcessionId, false);
    }
  }, []); // Dependencias vacías para ejecutar solo en mount

  // Auto-fill function - fills Description fields from genericOrder/campaign
  const fetchToFillData = useCallback(async () => {
    try {
      const sourceData = autoFillData?.genericOrder || autoFillData?.campaignData;
      if (!sourceData) return;

      // Fetch concession and road data
      const [
        { data: { concession } },
        { data: { road } },
      ] = await Promise.all([
        client.query({
          query: GET_CONCESSION_BY_ID,
          variables: { id: sourceData.concessionIDs[0] },
        }),
        client.query({
          query: GET_ROAD_BY_ID,
          variables: { id: sourceData.roadIDs[0] },
        }),
      ]);

      // Set concession
      concessionDropDownRef.current?.setSelectedOption({
        label: concession.name,
        value: concession.id,
        data: concession.roadIDs,
      });
      setValue('concession', sourceData.concessionIDs[0]);
      setValue('city', concession.location);

      // Load roads for this concession
      let readRoads = client.readQuery({
        query: GET_ROADS_BY_IDS,
        variables: { ids: concession.roadIDs ?? [] },
      })?.readRoads;

      if (!readRoads || readRoads.length === 0) {
        await fetchDefaultData();
        readRoads = client.readQuery({
          query: GET_ROADS_BY_IDS,
          variables: { ids: concession.roadIDs ?? [] },
        })?.readRoads;
      }

      if (readRoads && readRoads.length > 0) {
        setValue('roads', readRoads);
      }

      // Set road
      roadDropDownRef.current?.setSelectedOption({
        label: road.name,
        value: road.id,
      });
      setValue('road', sourceData.roadIDs[0]);
      setValue('pkInitRoad', pkFormatter(road.pkInitKm, road.pkInitMeter, true));
      setValue('pkEndRoad', pkFormatter(road.pkEndKm, road.pkEndMeter, true));
      setValue('roadDirections', road.directions);

      // Set PK values
      setValue(
        'pkInit',
        `${sourceData.pkInitKm}+${String(sourceData.pkInitMeter).padStart(3, '0')}`
      );
      setValue(
        'pkEnd',
        `${sourceData.pkEndKm}+${String(sourceData.pkEndMeter).padStart(3, '0')}`
      );

      // Set direction
      if (sourceData.direction) {
        setValue('direction', sourceData.direction as CommunicationDirection);
      }

      // Set margin
      if (sourceData.margin) {
        setValue('margin', sourceData.margin as NewCommunication['margin']);
      }

      // Set track (tipo vial)
      const findOptionTrack = groupTracks.find(
        (track) => track.value === sourceData.track
      );
      if (findOptionTrack) {
        trackDropDownRef.current?.setSelectedOption({
          label: trackLabel(findOptionTrack.value, t),
          value: findOptionTrack.value,
        });
        setValue('track', findOptionTrack.value);
      }

      // Set lane (carril)
      const findOptionLane = groupLanes.find(
        (lane) => lane.value === sourceData.lane
      );
      if (findOptionLane) {
        laneDropDownRef.current?.setSelectedOption({
          label: laneLabel(findOptionLane.value, t),
          value: findOptionLane.value,
        });
        setValue('lane', findOptionLane.value);
      }

      // Set description from parent order
      if (autoFillData?.order?.description) {
        setValue('description', autoFillData.order.description);
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching auto-fill data in Description:', error);
    }
  }, [autoFillData, setValue]);

  // Trigger auto-fill when autoFillData changes
  useEffect(() => {
    if (autoFillData?.genericOrder || autoFillData?.campaignData) {
      fetchToFillData();
    }
  }, [autoFillData, fetchToFillData]);

  return (
    <ContainerDescription>
      <Title component="h2" variant="semiBold" size="L">
        {t('communication.description')}
      </Title>
      <InputsContainer>
        <LocationInputGroupCommunication>
          <ConcessionWrapper>
            <Controller
              control={control}
              name="concession"
              render={({
                field: { onChange, value },
                formState: { defaultValues },
              }) => {
                const options =
                  defaultValues?.concessions?.reduce((acc, curr) => {
                    if (curr?.name !== 'CX') {
                      acc.push({
                        label: curr?.name || '',
                        value: curr?.id || '',
                      });
                    }

                    return acc;
                  }, [] as GroupTypes[]) || [];

                // Si solo hay una concesión, auto-seleccionar y no mostrar nada
                if (options.length === 1) {
                  const concessionId = options[0].value;
                  if (value !== concessionId) {
                    onChange(concessionId);
                  }
                  return <></>;
                }

                // Si hay múltiples concesiones, mostrar dropdown
                return (
                  <DropDownInput
                    inputSize="XS"
                    onChangeSelected={(option) =>
                      onSelectConcession(option, onChange)
                    }
                    typeDropDown="Default"
                    labelText={t('user.concession')}
                    placeholder={t('common:select')}
                    defaultValue={defaultValues?.concession}
                    borderError={!!errors.concession}
                    options={options}
                  />
                );
              }}
            />
          </ConcessionWrapper>
          <InputText
            {...register('city')}
            label={t('workOrder.location')}
            inputSize="S"
            typeInput="normal"
            borderError={!city && !!errors.city}
            disabled
          />
        </LocationInputGroupCommunication>
        <LocationInputGroupCommunication>
          <RoadInputSubGroup>
            <Controller
              control={control}
              name="road"
              render={({
                field: { onChange },
                formState: { defaultValues },
              }) => {
                const options =
                  roads?.map((road) => ({
                    label: road?.name || '',
                    value: road?.id || '',
                  })) || [];

                return (
                  <DropDownInput
                    ref={roadDropDownRef}
                    inputSize="XS"
                    onChangeSelected={(option) =>
                      onSelectRoad(option, onChange)
                    }
                    typeDropDown="Default"
                    labelText={t('workOrder.road')}
                    placeholder={t('common:select')}
                    defaultValue={defaultValues?.road}
                    borderError={!!errors.road}
                    options={options}
                  />
                );
              }}
            />
            <InputText
              {...register('pkInitRoad')}
              label={t('workOrder.start')}
              inputSize="12"
              typeInput="normal"
              placeholder={t('common:pkExample')}
              borderError={!pkInitRoad && !!errors.pkInitRoad}
              disabled
            />

            <InputText
              {...register('pkEndRoad')}
              label={t('workOrder.end')}
              inputSize="12"
              typeInput="normal"
              placeholder={t('common:pkExample')}
              borderError={!pkEndRoad && !!errors.pkEndRoad}
              disabled
            />
          </RoadInputSubGroup>
        </LocationInputGroupCommunication>
        <LocationInputGroupCommunication>
          <Controller
            control={control}
            name="track"
            render={({ field: { onChange }, formState: { defaultValues } }) => {
              const options = translateTrackOptions(
                defaultValues?.groupTracks as CatalogEntry[] | undefined,
                t
              );
              return (
                <SearchableDropdown
                  ref={trackDropDownRef}
                  inputSize="M"
                  onChangeSelected={(option) => onChange(option?.value ?? '')}
                  typeDropDown="Default"
                  labelText={t('workOrder.roadType')}
                  placeholder={t('common:labels.select')}
                  defaultValue={defaultValues?.track}
                  borderError={!!errors.track}
                  options={options}
                />
              );
            }}
          />
        </LocationInputGroupCommunication>
        <LocationInputGroupCommunication>
          <Controller
            control={control}
            name="direction"
            render={({ field: { onChange, value }, formState: { defaultValues } }) => {
              // Use current field value (updates with setValue) or fall back to initial defaultValues
              const currentDirection = value || defaultValues?.direction;
              const defaultStatusValue: PartialDirection = {
                [CommunicationDirection.DOWN]: 'down',
                [CommunicationDirection.TOP]: 'top',
                [CommunicationDirection.BOTH]: 'both',
              };

              const directionOptions: PartialDirection = {
                down: CommunicationDirection.DOWN,
                top: CommunicationDirection.TOP,
                both: CommunicationDirection.BOTH,
              };

              return (
                <ArrowRadioGroup
                  directions={['down', 'both', 'top']}
                  name="direction"
                  label={t('workOrder.direction')}
                  defaultValue={
                    defaultStatusValue[
                      currentDirection as CommunicationDirection
                    ]
                  }
                  borderError={!!errors.direction}
                  onChangeDirection={(dir) => {
                    onChange(directionOptions[dir]);
                  }}
                  directionLabels={{
                    down: roadDirections?.[0],
                    top: roadDirections?.[roadDirections?.length - 1],
                  }}
                />
              );
            }}
          />
        </LocationInputGroupCommunication>
        {showMargin && (
          <LocationInputGroupCommunication>
            <Controller
              control={control}
              name="margin"
              render={({ field: { onChange }, formState: { defaultValues } }) => {
                const defaultStatusValue: PartialDirection = {
                  [Margin.BOTH]: 'both',
                  [Margin.LEFT]: 'left',
                  [Margin.RIGHT]: 'right',
                  [Margin.UNSPECIFIC]: UNSPECIFIC,
                };

                const options: PartialDirection = {
                  both: Margin.BOTH,
                  left: Margin.LEFT,
                  right: Margin.RIGHT,
                  [UNSPECIFIC]: UNSPECIFIC,
                };

                return (
                  <ArrowRadioGroup
                    directions={['left', 'both', 'right', UNSPECIFIC]}
                    hasUnspecific
                    name="margin"
                    label={t('workOrder.margin')}
                    defaultValue={
                      defaultStatusValue[defaultValues?.margin as Margin]
                    }
                    borderError={!!errors.margin}
                    onChangeDirection={(dir) => {
                      onChange(options[dir]);
                    }}
                  />
                );
              }}
            />
          </LocationInputGroupCommunication>
        )}
        <LocationInputGroupCommunication>
          <Controller
            control={control}
            name="lane"
            render={({ field: { onChange }, formState: { defaultValues } }) => {
              const options = translateLaneOptions(
                defaultValues?.groupLanes as CatalogEntry[] | undefined,
                t
              );
              return (
                <SearchableDropdown
                  ref={laneDropDownRef}
                  inputSize="S"
                  onChangeSelected={(option) => onChange(option?.value ?? '')}
                  typeDropDown="Default"
                  labelText={t('workOrder.lane')}
                  placeholder={t('common:labels.select')}
                  defaultValue={defaultValues?.lane}
                  borderError={!!errors.lane}
                  options={options}
                />
              );
            }}
          />
        </LocationInputGroupCommunication>
        <LocationInputGroupCommunication>
          <PkInputContainer>
            <InputText
              {...registerPk(register, setValue, 'pkInit')}
              label={t('workOrder.pkInitial')}
              inputSize="15"
              typeInput="normal"
              placeholder={t('common:pkExample')}
              borderError={!!errors.pkInit}
            />

            <InputText
              {...registerPk(register, setValue, 'pkEnd')}
              label={t('workOrder.pkFinal')}
              inputSize="15"
              typeInput="normal"
              placeholder={t('common:pkExample')}
              borderError={!!errors.pkEnd}
            />
          </PkInputContainer>
        </LocationInputGroupCommunication>

        {(errors.pkInit || errors.pkEnd) && (
          <ErrorMessage size="XS" colorText="red">
            *{t('common:messages.formatError')}
          </ErrorMessage>
        )}
      </InputsContainer>

      <InputTextAreaStyled
        {...register('description', {})}
        name="description"
        disabled={false}
        label={t('workOrder.description')}
        placeholder={t('common:text')}
        error={errors.description?.message}
        borderError={!!errors.description}
      />
      <InputTextAreaStyled
        {...register('notes', {})}
        name="notes"
        disabled={false}
        label={t('workOrder.notes')}
        placeholder={t('common:text')}
        error={errors.notes?.message}
        borderError={!!errors.notes}
        defaultValue={initialData?.notes}
      />
    </ContainerDescription>
  );
};
